import axios from "axios";
import {cloneDeep} from "lodash";

const API_BUILDER_URL = '/api/parser/grid_builder';
const API_WELLS_URL = '/api/parser/wells_builder';
const API_CONSTANTS_URL = '/api/parser/constants_builder';
const API_CONNECTIONS_URL = '/api/calc/connections';

const DEFAULT_CONSTANTS = {
    sw: null,
    so: null,
    mu_w: null, //вязкость воды
    mu_o: null, //вязкость нефти
    cw: null, //сжимаемость воды
    co: null, //сжимаемость нефти
    cr: null, //сжимаемость породы
    p: null, //давление в пласте
    dd: null, //глубина приведения, для которой указано давление
    k: null, //проницаемость -- считается средней по ячейкам
    phi: null, //пористость -- считается средней по ячейкам
    rho_w: null,
    rho_w_sc: null,
    rho_o: null,
    rho_o_sc: null
}

const DEFAULT_OFP_CONSTANTS = {
    a: 0.6,
    no: 2,
    nw: 2
}

const DEFAULT_UNITS = {
    "length": "m",
    "volume": "m^3",
    "viscosity": "mPa*s",
    "compressibility": "bara^-1",
    "permeability": "mD",
    "pressure": "bara",
    "density": "kg/m^3",
}

export default {
    namespaced: true,
    state: {
        grid: null,
        gridMeshFile: null,
        parsedProblem: null,
        injectionData: null,
        historyData: null,
        bhpData: null,
        varsData: null,
        gridProperties: null,
        lastParams: null,
        wells: null,
        loading: false,
        connectionsLoading: false,
        connectionsAutoload: true,
        loadCounter: 0,
        processing: false,
        gridFileError: null,
        shapeFileError: null,
        rockFileError: null,
        wellsFileError: null,
        swsoFileError: null,
        pFileError: null,
        fluidFileError: null,
        oneStepFileError: null,
        connectionsError: null,
        constants: cloneDeep(DEFAULT_CONSTANTS),
        inputConstants: cloneDeep(DEFAULT_CONSTANTS),
        ofpConstants: cloneDeep(DEFAULT_OFP_CONSTANTS),
        ofpPoints: null,
        controlPressures: null,
        connections: null,
        nodes: null,
        units: DEFAULT_UNITS,
        fontSize: null,
        wellMarkerSize: null,
    },
    actions: {
        async loadGridMesh({commit, dispatch}, {problemId, size, cellSize, isParsed}) {
            await commit('setLoading', true);
            await commit('setErrorByTarget', {
                target: 'shape',
                error: null,
            });

            let gridBlob = null;
            try {
                let meshUrl = '/storage/mesh/'+problemId+'/grid.zip';
                let response = await axios.get(meshUrl, {responseType: 'blob'});
                if (response.data) {
                    gridBlob = new Blob([response.data]);
                    if (gridBlob.size > 0) {
                        await commit('setGrid', gridBlob);
                    }
                    else {
                        await commit('setGrid', null);
                    }
                }
                else {
                    await commit('setGrid', null);
                }
            }
            catch (e) {
                await commit('setGrid', null);
                await commit('setProcessing', false);
                await commit('setErrorByTarget', {
                    target: 'shape',
                    error: e.toString(),
                });
            }
            finally {
                await commit('setLoading', false);
            }

            return gridBlob;
        },
        async loadGridIndexes({commit, state}, problemId) {
            try {
                let response = await axios.get('/storage/mesh/'+problemId+'/indexes.json');
                if (response.data) {
                    let indexes = response.data || {};
                    let newGridProperties = Object.assign(state.gridProperties, indexes);
                    await commit('setGridProperties', newGridProperties);
                }
            }
            catch (e) {}
        },
        async updateGrid({commit}, params) {
            await commit('setLoading', true);
            await commit('resetAllErrors');

            params.grid_properties = params.grid_properties || {}
            params.grid_properties.only_active = true;
            commit('setLastParams', params);

            try {
                let response = await axios.post(API_BUILDER_URL, params);
                if (response.data) {
                    if (response.data.grid_properties) {
                        await commit('setGridProperties', response.data.grid_properties);
                    }
                    else {
                        await commit('setGridProperties', null);
                    }

                    if (response.data && response.data.wells) {
                        await commit('setWells', response.data.wells);
                    }
                    else {
                        await commit('setWells', null);
                    }

                    if (response.data.constants) {
                        await commit('setConstants', response.data.constants);
                    }
                    else {
                        await commit('setConstants', null);
                    }

                    if (response.data.relative_perm) {
                        await commit('setRelativePerm', response.data.relative_perm);
                    }
                    else {
                        await commit('setRelativePerm', null);
                    }

                    if (response.data.schedule_data) {
                        await commit('setInjectionData', response.data.schedule_data);
                    }
                    else if (response.data.injection_data) {
                        await commit('setInjectionData', response.data.injection_data);
                    }
                    else {
                        await commit('setInjectionData', null);
                    }

                    if (response.data.oil_history_data) {
                        await commit('setHistoryData', response.data.oil_history_data);
                    }
                    else if (response.data.history_data) {
                        await commit('setHistoryData', response.data.history_data);
                    }
                    else {
                        await commit('setHistoryData', null);
                    }

                    if (response.data.bhp_data) {
                        await commit('setBhpData', response.data.bhp_data);
                    }
                    else {
                        await commit('setBhpData', null);
                    }

                    if (response.data.vars_data) {
                        await commit('setVarsData', response.data.vars_data);
                    }
                    else {
                        await commit('setVarsData', null);
                    }

                    if (response.data.bhp_control) {
                        await commit('setControlPressures', response.data.bhp_control);
                    }
                    else {
                        await commit('setControlPressures', null);
                    }

                    if (response.data.ofp_points) {
                        await commit('setOfpPoints', response.data.ofp_points);
                    }
                    else {
                        await commit('setOfpPoints', null);
                    }


                    if (response.data.problem) {
                        await commit('setParsedProblem', response.data.problem);
                    }
                    else {
                        await commit('setParsedProblem', null);
                    }

                    if (response.data.error) {
                        await commit('setErrorByTarget', {
                            target: params.target,
                            error: response.data.error,
                        });
                    }
                }
            }
            catch (e) {
                commit('setProcessing', false);
                commit('setErrorByTarget', {
                    target: params.target,
                    error: e.toString(),
                });
            }

            await commit('setLoading', false);
        },
        async reloadGrid({dispatch, state}, updatedParams = {}) {
            let lastParams = state.lastParams || {};
            let params = Object.assign(lastParams, updatedParams || {});

            params.grid_properties = state.gridProperties;

            return dispatch('updateGrid', params);
        },
        async updateWells({commit}, params) {
            await commit('setLoading', true);
            await commit('setWellsFileError', null);

            try {
                let response = await axios.post(API_WELLS_URL, params);
                if (response.data && response.data.wells) {
                    await commit('setWells', response.data.wells);
                }
                else if (response.data && response.data.error) {
                    await commit('setWellsFileError', response.data.error);
                }
            }
            catch (e) {
                await commit('setWellsFileError', e.toString());
            }

            await commit('setLoading', false);
        },
        async updateConstants({commit}, params) {
            await commit('setLoading', true);
            await commit('setErrorByTarget', {
                target: params.target,
                error: null,
            });

            try {
                let response = await axios.post(API_CONSTANTS_URL, params);
                if (response.data && response.data.constants) {
                    await commit('setConstants', response.data.constants);
                }
                else if (response.data && response.data.error) {
                    commit('setErrorByTarget', {
                        target: params.target,
                        error: response.data.error,
                    });
                }
            }
            catch (e) {
                commit('setErrorByTarget', {
                    target: params.target,
                    error: e.toString(),
                });
            }

            await commit('setLoading', false);
        },
        async updateConnections({commit}, params) {
            let wells = params?.wells || [];
            let method = params?.method || 'insim';
            let target = params?.target || 'nodes';
            let problem = params?.problem || null;
            params = Object.assign(params, {wells, method, target, problem});

            await commit('setConnectionsLoading', true);
            await commit('setErrorByTarget', {
                target: 'connections',
                error: null,
            });

            let connections = null;
            let nodes = null;
            try {
                let response = await axios.post(API_CONNECTIONS_URL, params);
                if (response.data && response.data.connections) {
                    connections = response.data.connections;
                    nodes = response.data.nodes;
                    await commit('setConnectionsAndNodes', connections, nodes);
                }
                else if (response.data && response.data.error) {
                    commit('setErrorByTarget', {
                        target: 'connections',
                        error: response.data.error,
                    });
                }
            }
            catch (e) {
                commit('setErrorByTarget', {
                    target: 'connections',
                    error: e.toString(),
                });
            }

            await commit('setConnectionsLoading', false);
            return {connections, nodes};
        },
        setGridData({commit}, gridProperties, allConstants, ofpPoints) {
            let constants = {}
            let ofpConstants = {}
            let ofpConstantsNames = Object.keys(DEFAULT_OFP_CONSTANTS);
            for (let constant in allConstants) {
                if (ofpConstantsNames.includes(constant)) {
                    ofpConstants[constant] = allConstants[constant];
                }
                else {
                    constants[constant] = allConstants[constant];
                }
            }

            commit('setGridProperties', gridProperties);
            commit('setConstants', constants);
            commit('setInputConstants', constants);
            commit('setOfpConstants', ofpConstants);
            commit('setOfpPoints', ofpPoints);
        }
    },
    mutations: {
        setGrid(state, grid) {
            state.grid = grid;
            state.gridMeshFile = grid;
        },
        setLastParams(state, params) {
            state.lastParams = params;
        },
        setGridProperties(state, gridProperties) {
            state.gridProperties = gridProperties;
        },
        setGridProperty(state, updateProperties) {
            for (let propertyName in updateProperties) {
                let propertyValue = updateProperties[propertyName];
                if (!state.gridProperties) {
                    state.gridProperties = {};
                }

                this._vm.$set(state.gridProperties, propertyName, propertyValue);
            }
        },
        setWells(state, wells) {
            state.wells = wells;
        },
        resetAllErrors(state) {
            state.gridFileError = null;
            state.shapeFileError = null;
            state.rockFileError = null;
            state.swsoFileError = null;
            state.pFileError = null;
            state.fluidFileError = null;
            state.oneStepFileError = null;
            state.connectionsError = null;
        },
        setErrorByTarget(state, {target, error}) {
            if (target === 'size') {
                state.gridFileError = error;
            }

            if (target === 'shape') {
                state.shapeFileError = error;
            }

            if (target === 'rock') {
                state.rockFileError = error;
            }

            if (target === 'swso') {
                state.swsoFileError = error;
            }

            if (target === 'p') {
                state.pFileError = error;
            }

            if (target === 'fluid') {
                state.fluidFileError = error;
            }

            if (target === 'oneStep') {
                state.oneStepFileError = error;
            }

            if (target === 'connections') {
                state.connectionsError = error;
            }

            if (state.loading) {
                state.loading = false;
            }
        },
        setWellsFileError(state, error) {
            state.wellsFileError = error;
        },
        setLoading(state, loading) {
            state.loading = loading;
            if (!loading) {
                state.loadCounter++;
            }
        },
        setConnectionsLoading(state, loading) {
            state.connectionsLoading = loading;
        },
        setConnectionsAutoload(state, autoload) {
            state.connectionsAutoload = autoload;
        },
        setFontSize(state, newSize) {
            state.fontSize = newSize;
        },
        setWellMarkerSize(state, newSize) {
            state.wellMarkerSize = newSize;
        },
        setProcessing(state, processing) {
            state.processing = processing;
        },
        setConstants(state, newConstants) {
            if (newConstants) {
                let updatedConstants = {};
                for (let constant in newConstants) {
                    if (newConstants[constant] !== null) {
                        updatedConstants[constant] = newConstants[constant];
                    }
                }
                state.constants = Object.assign(state.constants, updatedConstants);
            }
        },
        setConnectionsAndNodes(state, newConnections, newNodes) {
            state.connections = newConnections;
            state.nodes = newNodes;
        },
        setInputConstants(state, newConstants) {
            if (newConstants) {
                state.inputConstants = Object.assign(state.constants, newConstants);
            }
        },
        setOfpConstants(state, newConstants) {
            if (newConstants) {
                state.ofpConstants = Object.assign(state.ofpConstants, newConstants);
            }
        },
        setOfpPoints(state, newPoints) {
            state.ofpPoints = newPoints;
        },
        setRelativePerm(state, newRelativePerm) {
            if (newRelativePerm) {
                state.relativePerm = Object.assign(state.relativePerm, newRelativePerm);
            }
        },
        setInjectionData(state, injectionData) {
            state.injectionData = injectionData;
        },
        setHistoryData(state, historyData) {
            state.historyData = historyData;
        },
        setBhpData(state, bhpData) {
            state.bhpData = bhpData;
        },
        setVarsData(state, newVars) {
            state.varsData = newVars;
        },
        setParsedProblem(state, parsedProblem) {
            state.parsedProblem = parsedProblem;
        },
        setControlPressures(state, newPressures) {
            state.controlPressures = newPressures;
        },
        resetData(state) {
            state.grid = null;
            state.parsedProblem = null;
            state.injectionData = null;
            state.historyData = null;
            state.bhpData = null;
            state.varsData = null;
            state.gridProperties = null;
            state.lastParams = null;
            state.wells = null;
            state.loading = false;
            state.processing = false;
            state.inputConstants = cloneDeep(DEFAULT_CONSTANTS);
            state.constants = cloneDeep(DEFAULT_CONSTANTS);
            state.ofpConstants = cloneDeep(DEFAULT_OFP_CONSTANTS);
            state.ofpPoints = null;

            state.gridFileError = null;
            state.shapeFileError = null;
            state.rockFileError = null;
            state.swsoFileError = null;
            state.pFileError = null;
            state.fluidFileError = null;
            state.oneStepFileError = null;

            state.connections = null;
            state.nodes = null;
        }
    }
}
